import { Component, computed, ElementRef, OnInit, signal, ViewChild } from '@angular/core';
import { UserModel } from '../../../../core/models/data/user.model';
import { UserService } from '../../../../core/services/user.service';
import { CommonModule } from '@angular/common';
import { PaginationComponent } from '../../../../parts/pagination/pagination.component';
import { FormsModule } from '@angular/forms';
import { UserSearchFilterComponent } from "../user-search-filter/user-search-filter.component";
import { PaginatedListModel } from '../../../../core/models/service/paginated-list.model';
import { LocalStorageKeyConstants, PaginationConstants } from '../../../../core/constants/constants';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { InvitationStatusColors, UserInvitationStatuses, UserStatuses } from '../../../../core/enums/data.enums';
import { UserListSortOptions } from '../../../../core/enums/sort-options.enums';
import { AvatarConstants } from '../../../../core/constants/avatar.constants';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { InViewPortDirective } from '../../../../core/directives/in-view-port.directive';
import { ResponsiveTableDirective } from '../../../../core/directives/responsive-table/responsive-table.directive';
import { ResponsiveTableColumnDirective } from '../../../../core/directives/responsive-table/responsive-table-column.directive';
import { ConfirmationModalComponent } from "../../../../parts/confirmation-modal/confirmation-modal.component";
import { InfoModalComponent } from "../../../../parts/info-modal/info-modal.component";
import { SortDropdownComponent } from '../../../../parts/sort-dropdown/sort-dropdown/sort-dropdown';
import { AddUserComponent } from "../add-user/add-user.component";
import { ItemSelectionPipe } from "../../../../core/pipes/item-selection/item-selection-pipe";
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { AuthorizationService } from '../../../../core/services/authorization/authorization.service';
import { AuthorizationStateService } from '../../../../core/services/authorization/authorization-state.service';
import { PermissionUtilities } from '../../../../core/utilities/permission.utilities';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { AvatarComponent } from "../../../../parts/avatar/avatar.component";
import { ActivatedRoute, RouterLink } from '@angular/router';
import { BaseListPageClass } from '../../../../core/base-classes/base-list-page-class';
import { ServiceResultModel } from '../../../../core/models/service/service-result.model';
import { UserBulkActions } from '../../../../core/enums/bulk-actions.enums';
import { BulkActionRequest } from '../../../../core/models/service/bulk-action-request.model';
import { UsersFilter } from '../../../../core/filters/users.filter';
import { DisableWithoutPermissionDirective } from '../../../../core/directives/disable-without-permission/disable-without-permission.directive';
import { UserPermission } from '../../../../core/enums/permissions.enums';
import { UsersMessagingConstants } from '../../../../core/constants/messaging.constants';
import { StatusBadgeComponent } from '../../../../parts/status-badge/status-badge.component';
import { TooltipDirective } from '../../../../core/directives/tooltip.directive';
import { PageTitleComponent } from '../../../../parts/page-title/page-title.component';

@Component({
  selector: 'ms-user-list.page',
  imports: [
    PageTitleComponent,
    FontAwesomeModule,
    PaginationComponent,
    FormsModule,
    UserSearchFilterComponent,
    CommonModule,
    InViewPortDirective,
    ConfirmationModalComponent,
    InfoModalComponent,
    SortDropdownComponent,
    AddUserComponent,
    ItemSelectionPipe,
    AvatarComponent,
    RouterLink,
    DisableWithoutPermissionDirective,
    ResponsiveTableDirective,
    ResponsiveTableColumnDirective,
    StatusBadgeComponent,
    TooltipDirective
  ],
  templateUrl: './user-list.component.html',
  host: { "style": "display:contents" }
})
export class UserListPage extends BaseListPageClass implements OnInit {
  //Component Data
  public userList = signal<UserModel[]>([]);
  public newUserStatus?: UserStatuses.Active | UserStatuses.Inactive | undefined;

  public targetedUser?: UserModel = undefined;

  public UserStatuses = UserStatuses;
  public UserInvitationStatuses = UserInvitationStatuses;
  public InvitationStatusColors = InvitationStatusColors;
  public UserBulkActions = UserBulkActions;
  public UserPermission = UserPermission;

  // Staff (user-level users.status) may deactivate their own account; drives the self-exclusion note.
  public canDeactivateOwnAccount = computed(() => PermissionUtilities.hasUserLevelPermission(
    UserPermission.Status, this.authorizationService, this.authorizationStateService));

  // Per-user protection results, populated lazily as each row's Actions dropdown is opened — avoids
  // firing an authorization lookup for every row on list load. Unlike Members, deactivating a user
  // does not clear their roles, so Reactivate needs this guard too, not just Deactivate/Delete/Edit
  // (DEV-2541).
  private protectedUserCache = signal<Map<number, boolean>>(new Map());

  // True when the row is the logged-in user's own account; used to skip the authority-level check
  // entirely (it resolves synchronously to "not protected" — self-actions are governed separately
  // by canDeactivateOwnAccount()).
  public isOwnAccount(user: UserModel): boolean {
    return this.userSessionService.isCurrentUser(user.userId);
  }

  // A user cannot be reactivated/deactivated/deleted/edited by a viewer whose user-level authority
  // isn't stronger than theirs (e.g. Staff acting on a System Administrator). Stays false until
  // onActionsDropdownOpened resolves the check for this row — isCheckingProtection() below covers
  // the interim; the backend's own authority check remains the actual safeguard regardless.
  public isProtectedFromModification(user: UserModel): boolean {
    return this.protectedUserCache().get(user.userId) ?? false;
  }

  // True while this row's authorization check hasn't resolved yet (own account excluded — that
  // resolves synchronously with no fetch).
  public isCheckingProtection(user: UserModel): boolean {
    return !this.isOwnAccount(user) && !this.protectedUserCache().has(user.userId);
  }

  public async onActionsDropdownOpened(user: UserModel): Promise<void> {
    if (this.isOwnAccount(user) || this.protectedUserCache().has(user.userId)) return;

    const targetAuthorityLevel = await PermissionUtilities.fetchTargetUserAuthorityLevel(user.userId, this.authorizationService);
    const isProtected = PermissionUtilities.isProtectedFromUserModification(targetAuthorityLevel, this.authorizationStateService);

    this.protectedUserCache.update(cache => new Map(cache).set(user.userId, isProtected));
  }

  // #region Row Action Visibility
  // Reactivate, Deactivate and Delete are hidden rather than greyed out when the viewer isn't
  // authorised to take them (DEV-2576). That covers a target whose authority they can't outrank — a
  // Staff or System Administrator account — and, for status changes, their own record. Resolved here
  // rather than through the permission directives so a row never renders an action label the viewer
  // can't use. View is the exception and stays on disableWithoutPermission: it needs users.read,
  // which anyone who reached this list already holds, so there is nothing to hide.

  // The same rule reaches the List Actions dropdown: Staff hold users.read/update/delete but no longer
  // users.create or users.status, so Add User and the bulk Activate/Deactivate are hidden from them
  // rather than greyed out.
  public canCreateUsers = computed(() => PermissionUtilities.hasUserLevelPermission(
    UserPermission.Create, this.authorizationService, this.authorizationStateService));

  public canUpdateUserStatus = computed(() => PermissionUtilities.hasUserLevelPermission(
    UserPermission.Status, this.authorizationService, this.authorizationStateService));

  private canDeleteUsers = computed(() => PermissionUtilities.hasUserLevelPermission(
    UserPermission.Delete, this.authorizationService, this.authorizationStateService));

  // While the row's authority check is still in flight we show a neutral placeholder instead of the
  // real actions — a label that appears and then vanishes reads as a glitch, and worse, invites a
  // click on something the viewer was never allowed to do. Only shown when the viewer actually
  // holds an action that depends on the check.
  public isResolvingRowActions(user: UserModel): boolean {
    return this.isCheckingProtection(user) && this.hasProtectedActionForStatus(user);
  }

  // Holding the permission isn't enough for the placeholder to be worth showing: each action also
  // needs the row to be in a status it applies to. A deleted account passes none of them, so without
  // this it would flash "Checking access..." and then settle into an empty dropdown.
  private hasProtectedActionForStatus(user: UserModel): boolean {
    const statusChangeApplies = this.canUpdateUserStatus()
      && (user?.status === UserStatuses.Active || user?.status === UserStatuses.Inactive);
    const deleteApplies = this.canDeleteUsers() && user?.status !== UserStatuses.Deleted;

    return statusChangeApplies || deleteApplies;
  }

  public canShowReactivate(user: UserModel): boolean {
    return this.canUpdateUserStatus()
      && user?.status === UserStatuses.Inactive
      && this.canActOnUserRecord(user);
  }

  public canShowDeactivate(user: UserModel): boolean {
    return this.canUpdateUserStatus()
      && user?.status === UserStatuses.Active
      && this.canActOnUserRecord(user);
  }

  public canShowDelete(user: UserModel): boolean {
    return this.canDeleteUsers()
      && user?.status !== UserStatuses.Deleted
      && this.canActOnUserRecord(user);
  }

  private canActOnUserRecord(user: UserModel): boolean {
    return !this.isOwnAccount(user) && !this.isProtectedFromModification(user);
  }
  // #endregion Row Action Visibility

  public userBulkAction?: UserBulkActions;

  //Confirmation Modal
  public confirmationCancelFunction: Function = () => {};
  public confirmationConfirmFunction: Function = () => {};
  
  public isSubmitting = false;

  // Sort
  public userListSortOptions = UserListSortOptions;

  //Avatar
  public avatarSize = AvatarConstants.SIZE().USER_LIST;
  public avatarShape = AvatarConstants.SHAPE().USER_LIST;
  public avatarAdditionalAttributes = AvatarConstants.ADDITIONAL_ATTRIBUTES().USER_LIST;

  public skeletonUsers: UserModel[] = [];

  @ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME().replace(/^#/, '')) jumpToElementRef!: ElementRef;
  @ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME().replace(/^#/, '')) jumpToElementMobileRef!: ElementRef;

  @ViewChild('userListConfirmationModalRef') confirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('userListInfoModalRef') infoModalRef!: InfoModalComponent;
  @ViewChild(AddUserComponent) addUserRef!: AddUserComponent;

  constructor(
    private userService: UserService,
    public userSessionService: UserSessionService,
    private bootstrapService: BootstrapService,
    private activatedRoute: ActivatedRoute,
    private authorizationService: AuthorizationService,
    private authorizationStateService: AuthorizationStateService) {
      super();
    }

  public ngOnInit(): void {
    this.createSkeletonUsers();
    this.orderBy = this.userSessionService.usersOrderBy;
    this.sortOptions = [...ListConstants.USERS_SORT_OPTIONS()];
    // Instead of loadUsers being called here we are calling it from the filter control
    // then firing it's filterApplied event since we are wanting default filters on the initial page load

    // ?action=add opens the Add User modal without going through the dropdown, so it needs the same
    // permission check the hidden menu item now carries — otherwise the link reopens a form the
    // viewer can't submit.
    if (this.canCreateUsers()) {
      this.openAddModalFromQueryParams(this.activatedRoute, '#addUserModal', () => this.addUserRef.openAddUserModal());
    }
  }

  public onAddUserClicked(): void {
    this.addUserRef.openAddUserModal();
  }

  private createSkeletonUsers(): void {
    this.skeletonUsers = Array.from({ length: this.take }, (_, index) => ({
      userId: index,
      firstName: 'Loading',
      lastName: 'User',
      emailAddress: 'loading@example.com',
      phoneNumber: '000-000-0000',
      status: UserStatuses.Active,
      lastLogin: new Date(),
      avatarFileId: undefined
    } as UserModel));
  }

  public get displayUsers(): UserModel[] {
    const userListValue = this.userList();
    if (this.isLoading && (!userListValue || userListValue.length === 0)) {
      return this.skeletonUsers;
    }
    return userListValue || [];
  }

  public clearSearch(): void {
    this.userSessionService.usersFilter.searchTerm = "";
  }

  public async loadUsers(append: boolean = false): Promise<void> {
    this.isLoading = true;

    if (append === false) { this.resetPagination(); }

    const response: ServiceResultModel<PaginatedListModel<UserModel>> = await this.userService.searchUsers(this.userSessionService.usersFilter, this.orderBy, this.skip(), this.take);

    this.totalCount = response?.data?.total ?? 0;
    this.hasNext = response?.data?.hasNext ?? false;

    if (response?.data?.data) {
      if (append == true) {
        this.userList.set(this.userList().concat(response?.data?.data!));
      } else {
        this.userList.set(response?.data?.data!);
        //Clear Selected Items
        this.isSelectAll = false;
        this.selectionItems.set([]);
      }
    }

    this.isLoading = false;
    this.calculateSelected();
  }

  public async loadMore(): Promise<void> {
    this.loadUsers(true);
  }

  public jumpToTop(element: string): void {
    switch (element) {
      case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME():
        this.jumpToElementRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
        break;

      case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME():
        this.jumpToElementMobileRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
        break;
    }
  }

  public filterApplied(): void {
    this.loadUsers();
  }
  
  public toggleSortDirection(columnName: string): void {
    const columnSortMap: Record<string, [UserListSortOptions, UserListSortOptions]> = {
      "Name": [UserListSortOptions.NameASC, UserListSortOptions.NameDESC],
      "Role": [UserListSortOptions.RoleASC, UserListSortOptions.RoleDESC],
      "Status": [UserListSortOptions.StatusASC, UserListSortOptions.StatusDESC],
      "Organizations": [UserListSortOptions.OrganizationASC, UserListSortOptions.OrganizationDESC],
      "Last Login": [UserListSortOptions.LastLoginASC, UserListSortOptions.LastLoginDESC]
    };

    const [ascOption, descOption] = columnSortMap[columnName]
      // fall back to default if columnName isn't present
      || [UserListSortOptions.LastLoginASC, UserListSortOptions.LastLoginDESC];

    let sortDirection: UserListSortOptions;

    if (this.orderBy === descOption) {
      sortDirection = ascOption;
    } else if (this.orderBy === ascOption) {
      sortDirection = descOption;
    } else {
      sortDirection = ascOption;
    }

    this.setSortOrder(sortDirection);
  }

  public setSortOrder(userListSortOption: string) {
    this.orderBy = userListSortOption;
    this.userSessionService.usersOrderBy = this.orderBy;
    this.userSessionService.saveToLocalStorage(LocalStorageKeyConstants.USERS_ORDERBY(), this.orderBy);
    this.filterApplied();
  }

  // #region Activate/Deactivate
  public confirmUserActivationStatusUpdate(newUserStatus: UserStatuses.Active | UserStatuses.Inactive, user: UserModel): void {
    const actionText = newUserStatus === UserStatuses.Active ? 'activate' : 'deactivate';
    const confirmationMessageData = {actionText: actionText, firstName: user.firstName, lastName: user.lastName};
    const confirmationMessage = 
      UsersMessagingConstants.USERS_UPDATE_ACTIVATION_STATUS_CONFIRMATION().replaceValuesWithObject(confirmationMessageData);

    this.setActivationConfirmationModalFunctions();
    this.newUserStatus = newUserStatus;
    this.targetedUser = user;

    this.confirmationModalRef.modalType.set(ModalTypes.Question);
    this.confirmationModalRef.message.set(confirmationMessage);

    this.confirmationModalRef.enableNotes.set(true);
    this.confirmationModalRef.isNoteRequired.set(false);
    // The modal is shared with the bulk actions, which turn this on.
    this.confirmationModalRef.enableTypeToConfirm.set(false);

    this.bootstrapService.manageModal("#userListConfirmationModal", ModalActions.Show);
  }

  public setActivationConfirmationModalFunctions(): void {
    this.confirmationConfirmFunction = this.confirmedUserStatusConfirmation;
    this.confirmationCancelFunction = this.cancelUserStatusConfirmation;
  }

  public cancelUserStatusConfirmation(): void {
    this.bootstrapService.manageModal("#userListConfirmationModal", ModalActions.Hide);
  }

  public async confirmedUserStatusConfirmation(note: string | undefined): Promise<void> {
    this.isSubmitting = true;
    try {
      const response = await this.userService.updateUserStatus(this.targetedUser?.userId!, this.newUserStatus === UserStatuses.Active, note);

      if (response.success === false) {
        // Error response received
        this.infoModalRef.modalType.set(ModalTypes.Failure);
      } else {
        // No error response
        this.infoModalRef.modalType.set(ModalTypes.Success);
        this.selectionItems.set([]);
        this.loadUsers();
      }

      this.infoModalRef.message.set(response.message);
      this.bootstrapService.manageModal("#userListInfoModal", ModalActions.Show);
      this.bootstrapService.manageModal("#userListConfirmationModal", ModalActions.Hide);
    } finally {
      this.isSubmitting = false;
    }
  }
  // #endregion Activate/Deactivate

  // #region Delete User
  public confirmUserDeletion(user: UserModel): void {
    const confirmationMessageData = {firstName: user.firstName, lastName: user.lastName};
    const confirmationMessage = 
      UsersMessagingConstants.USERS_DELETE_USER_CONFIRMATION().replaceValuesWithObject(confirmationMessageData);
    
    this.setDeletionConfirmationModalFunctions();
    
    this.targetedUser = user;

    this.confirmationModalRef.modalType.set(ModalTypes.Question);
    this.confirmationModalRef.message.set(confirmationMessage);

    this.confirmationModalRef.enableNotes.set(true);
    this.confirmationModalRef.isNoteRequired.set(false);
    // The modal is shared with the bulk actions, which turn this on.
    this.confirmationModalRef.enableTypeToConfirm.set(false);

    this.bootstrapService.manageModal("#userListConfirmationModal", ModalActions.Show);
  }

  public setDeletionConfirmationModalFunctions() : void {
    this.confirmationConfirmFunction = this.confirmedUserDeletionConfirmation;
    this.confirmationCancelFunction = this.cancelUserDeletionConfirmation;
  }

  public async confirmedUserDeletionConfirmation(note: string | undefined): Promise<void> {
    this.isSubmitting = true;
    try {
      const response = await this.userService.deleteUser(this.targetedUser!.userId, note);

      if (response.success === false) {
        //Error response received
        this.infoModalRef.modalType.set(ModalTypes.Failure);
      } else {
        //No error response
        this.infoModalRef.modalType.set(ModalTypes.Success);
        this.selectionItems.set([]);
        this.loadUsers();
      }

      this.infoModalRef.message.set(response.message);
      this.bootstrapService.manageModal("#userListInfoModal", ModalActions.Show);
      this.bootstrapService.manageModal("#userListConfirmationModal", ModalActions.Hide);
    } finally {
      this.isSubmitting = false;
    }
  }

  public cancelUserDeletionConfirmation(): void {
    this.bootstrapService.manageModal("#userListConfirmationModal", ModalActions.Hide);
  }
  // #endregion Delete User

  // #region Bulk Actions
  public confirmBulkAction(bulkAction: UserBulkActions): void {
    this.setBulkActivationConfirmationModalFunctions();

    this.userBulkAction = bulkAction;

    this.confirmationModalRef.enableNotes.set(true);
    this.confirmationModalRef.isNoteRequired.set(false);

    // Bulk status changes can sweep up a whole filter at once, so the caller types the action out first.
    this.confirmationModalRef.enableTypeToConfirm.set(true);
    this.confirmationModalRef.typeToConfirmText.set(bulkAction === UserBulkActions.Activate
      ? UsersMessagingConstants.USERS_BULK_ACTIVATE_TYPE_TO_CONFIRM()
      : UsersMessagingConstants.USERS_BULK_DEACTIVATE_TYPE_TO_CONFIRM());

    this.confirmationModalRef.modalType.set(ModalTypes.Question);
    const confirmationMessageData = {bulkAction: bulkAction, selectedCount: this.selectedCount()};
    let confirmationMessage =
      UsersMessagingConstants.USERS_BULK_ACTION_CONFIRMATION().replaceValuesWithObject(confirmationMessageData);

    // A restricted user can't deactivate their own account (the API skips it); note it alongside the count.
    if (bulkAction === UserBulkActions.Deactivate && !this.canDeactivateOwnAccount()) {
      confirmationMessage += ' ' + UsersMessagingConstants.USERS_BULK_SELF_EXCLUDED_NOTE();
    }

    this.confirmationModalRef.message.set(confirmationMessage);
    this.bootstrapService.manageModal("#userListConfirmationModal", ModalActions.Show);
  }

  public setBulkActivationConfirmationModalFunctions(): void {
    this.confirmationConfirmFunction = this.bulkActionModalConfirmed;
    this.confirmationCancelFunction = this.bulkActionModalCancelled;
  }

  public async bulkSetActivation(bulkAction: UserBulkActions, note: string | null = null): Promise<void> {
    this.isSubmitting = true;
    try {
      let bulkActionRequest: BulkActionRequest<UsersFilter | null> = {
        useFilter: this.isSelectAll,
        filter: (this.isSelectAll === true) ? this.userSessionService.usersFilter : null,
        selectedIds: (this.isSelectAll === false) ? this.selectionItems() : null,
        excludedIds: (this.isSelectAll === true) ? this.selectionItems() : null,
        note: note
      };

      const response = await this.userService.bulkActivationRequest(bulkAction, bulkActionRequest);

      if (response.success === false) {
        // Error response received
        this.infoModalRef.modalType.set(ModalTypes.Failure);
      } else {
        // No error response
        this.infoModalRef.modalType.set(ModalTypes.Success);
        this.selectionItems.set([]);
        this.loadUsers();
      }

      // On a successful deactivation, note the caller's own account was skipped — unless they may act on it.
      const selfNote = (response.success !== false && bulkAction === UserBulkActions.Deactivate && !this.canDeactivateOwnAccount())
        ? ' ' + UsersMessagingConstants.USERS_BULK_SELF_EXCLUDED_NOTE()
        : '';
      this.infoModalRef.message.set((response.message ?? '') + selfNote);
      this.bootstrapService.manageModal("#userListInfoModal", ModalActions.Show);
      this.bootstrapService.manageModal("#userListConfirmationModal", ModalActions.Hide);
    } finally {
      this.isSubmitting = false;
    }
  }

  public async bulkActionModalConfirmed(note: string | undefined): Promise<void> {
    await this.bulkSetActivation(this.userBulkAction!, note);
  }

  public bulkActionModalCancelled(): void {
    this.bootstrapService.manageModal("#userListConfirmationModal", ModalActions.Hide);
  }
  // #endregion Bulk Actions

  //#region table scrolling
  public loadMoreInView(): void {
    if (this.canLoadMore && this.hasNext) {
      this.loadMore();
      this.canLoadMore = false;
      this.loadMoreDebounce();
    }
  }
  //endregion
}
